import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Trash2 } from "lucide-react";
import { useI18n } from "@faceheart/i18n";
import { sitesApi, employeesApi } from "../lib/api/endpoints";
import { ApiError } from "../lib/api/client";
import { DeleteEmployeeDialog } from "../components/employees/DeleteEmployeeDialog";
import { TrendChart } from "../components/employees/TrendChart";
import { RadarCard } from "../components/employees/RadarCard";
import { MetricModal, RADAR_KEY } from "../components/employees/MetricModal";
import { METRICS, HEALTH_AXIS_KEYS } from "../components/employees/chartSetup";

type BpGroup = "normal" | "hypertension";
type Gender = "male" | "female" | "other";
type Range = "7d" | "30d" | "90d";
type Tab = "trends" | "radar";

const BP_VALUES: BpGroup[] = ["normal", "hypertension"];
const GENDER_VALUES: Gender[] = ["male", "female", "other"];

// Whole years from a birth year to today — mirrors the backend's ageFromBirthYear
// (employees.go) so the admin sees the same age the SDK gets. EmployeeDetail
// carries birth_year (not age), so we derive it client-side (ADR-031).
function ageFromBirthYear(year: number | null | undefined): number | null {
  if (year == null || !Number.isFinite(year)) return null;
  const age = new Date().getFullYear() - year;
  return age < 0 ? 0 : age;
}
const RANGE_VALUES: Range[] = ["7d", "30d", "90d"];
const TAB_VALUES: Tab[] = ["trends", "radar"];

// METRICS drives the line cards; the 6 health_* radar axes aren't in METRICS
// (they render as a radar, not lines) so we add them to the requested set
// explicitly (ADR-018 / SPEC-015 §3.6).
const METRIC_KEYS = [...METRICS.map((m) => m.key), ...HEALTH_AXIS_KEYS];

export function EmployeeDetailPage() {
  const { t } = useI18n("admin");
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [range, setRange] = useState<Range>("7d");
  // Trends (line charts) vs Health radar are split into two tabs on the detail
  // page (per request) — the radar is a different chart type and reads cleaner
  // on its own surface than mixed into the line grid.
  const [tab, setTab] = useState<Tab>("trends");
  // Zoom modal: holds the expanded card's key (a METRICS key or RADAR_KEY), or
  // null when closed (ADR-018 / SPEC-015 §3.4).
  const [expanded, setExpanded] = useState<string | null>(null);
  const qc = useQueryClient();

  const empQ = useQuery({
    queryKey: ["employee", id, "detail"],
    queryFn: () => employeesApi.get(id),
    enabled: Boolean(id),
  });

  // Inline edits all share the same shape: PATCH the one changed field,
  // invalidate the detail query, toast on result. Optimistic UI isn't worth it
  // for these once-in-a-while corrections. `field` keys the toast copy so each
  // edit reports itself; the generic onError reuses one failure string.
  // useFieldMut is a hook (calls useMutation); the 5 calls below are
  // unconditional and in fixed order, so Rules of Hooks hold.
  function useFieldMut<T>(field: string, body: (v: T) => Record<string, unknown>) {
    return useMutation({
      mutationFn: (v: T) => employeesApi.patch(id, body(v)),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ["employee", id, "detail"] });
        toast.success(t(`employeeDetail.${field}UpdateSuccess`));
      },
      onError: (err) =>
        toast.error(t(`employeeDetail.${field}UpdateFailed`), {
          description: (err as Error).message,
        }),
    });
  }

  const bpMut = useFieldMut<BpGroup>("bp", (bp_group) => ({ bp_group }));
  // birth_year / gender / height / weight are admin-editable since ADR-022 made
  // login code-only (birth_year is no longer the second factor). Age is derived
  // from birth_year, so editing it re-renders a fresh age (ADR-031).
  const birthMut = useFieldMut<number>("birth", (birth_year) => ({ birth_year }));
  const genderMut = useFieldMut<Gender>("gender", (gender) => ({ gender }));
  const heightMut = useFieldMut<number>("height", (height_cm) => ({ height_cm }));
  const weightMut = useFieldMut<number>("weight", (weight_kg) => ({ weight_kg }));

  // Face enrollment (SPEC-019 / ADR-028): admin uploads a face photo for this
  // employee. The admin attests PDPA consent (the checkbox gates the upload).
  const FACE_CONSENT_VERSION = "v1.0";
  const [faceConsent, setFaceConsent] = useState(false);
  const faceMut = useMutation({
    mutationFn: (frame: string) =>
      employeesApi.enrollFace(id, frame, FACE_CONSENT_VERSION),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["employee", id, "detail"] });
      toast.success(t("employeeDetail.faceUpdateSuccess"));
      setFaceConsent(false);
    },
    onError: (err) => {
      // The backend puts the quality-gate reason (no_face | multiple_faces |
      // low_quality) in ApiError.code on a 422. Surface a specific zh/en line so
      // the admin knows what to fix; fall back to the raw message otherwise.
      const code = err instanceof ApiError ? err.code : "";
      const known = ["no_face", "multiple_faces", "low_quality"];
      const description = known.includes(code)
        ? t(`employeeDetail.faceError.${code}`)
        : (err as Error).message;
      toast.error(t("employeeDetail.faceUpdateFailed"), { description });
    },
  });

  function onPickFace(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result ?? "");
      const base64 = dataUrl.split(",")[1] ?? "";
      if (base64) faceMut.mutate(base64);
    };
    reader.onerror = () =>
      toast.error(t("employeeDetail.faceUpdateFailed"), {
        description: t("employees.import.fileReadFailed"),
      });
    reader.readAsDataURL(f);
  }

  // Site options for the assign/reassign dropdown (SPEC-011 Phase 2).
  const sitesQ = useQuery({
    queryKey: ["sites"],
    queryFn: sitesApi.list,
  });

  // Assign/reassign site. Same shape as bpMut. Note: clearing back to
  // "unassigned" is NOT supported here — the backend's UpdateEmployeePartial
  // uses COALESCE(narg, …), so a null site_id keeps the existing value rather
  // than clearing it. We therefore only offer real sites below; the
  // "Unassigned" entry is render-only for employees with no site yet.
  const siteMut = useMutation({
    mutationFn: (site_id: string) => employeesApi.patch(id, { site_id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["employee", id, "detail"] });
      toast.success(t("employeeDetail.siteUpdateSuccess"));
    },
    onError: (err) =>
      toast.error(t("employeeDetail.siteUpdateFailed"), {
        description: (err as Error).message,
      }),
  });

  const baseQ = useQuery({
    queryKey: ["employee", id, "baselines"],
    queryFn: () => employeesApi.baselines(id),
    enabled: Boolean(id),
  });

  const trendQ = useQuery({
    queryKey: ["employee", id, "measurements", range],
    queryFn: () => employeesApi.measurements(id, range, METRIC_KEYS),
    enabled: Boolean(id),
  });

  if (!id) return null;
  if (empQ.isLoading)
    return <div className="p-8 text-admin-text2">{t("employeeDetail.loading")}</div>;
  if (empQ.error)
    return (
      <div className="p-8 text-admin-red">
        {t("employeeDetail.loadFailed", { message: (empQ.error as Error).message })}
      </div>
    );

  const e = empQ.data!;
  const baselines = baseQ.data ?? [];
  const seriesList = trendQ.data?.series ?? [];
  const baselineFor = (key: string) =>
    baselines.find((b) => b.metric === key);
  const seriesFor = (key: string) =>
    seriesList.find((s) => s.metric === key);

  return (
    <div className="p-8">
      <Link
        to="/employees"
        className="inline-flex items-center gap-1.5 text-sm text-admin-text2 hover:text-admin-cyan mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> {t("employeeDetail.backToList")}
      </Link>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-medium">{e.name}</h1>
            <span className="rounded bg-admin-bg3 border border-admin-border px-2 py-0.5 text-xs font-mono text-admin-text2">
              {e.employee_code ?? "—"}
            </span>
          </div>
          {/* Inline-editable basic profile (admin owns the record now — ADR-021).
              birth_year / gender / height / weight each PATCH on change/blur. */}
          <div className="flex items-center gap-x-4 gap-y-2 text-xs flex-wrap">
            <label className="flex items-center gap-1.5">
              <span className="text-admin-text3">
                {t("employeeDetail.birthYearLabel")}
              </span>
              <input
                type="number"
                inputMode="numeric"
                min={1900}
                max={new Date().getFullYear() - 12}
                defaultValue={e.birth_year}
                disabled={birthMut.isPending}
                onBlur={(ev) => {
                  const y = Number(ev.target.value);
                  if (Number.isInteger(y) && y !== e.birth_year) {
                    birthMut.mutate(y);
                  }
                }}
                className="bg-admin-bg3 border border-admin-border rounded px-2 py-1 w-20 text-admin-text1 disabled:opacity-50 focus:outline-none focus:border-admin-cyan"
              />
            </label>
            <span className="text-admin-text3">
              {ageFromBirthYear(e.birth_year) != null
                ? t("employeeDetail.ageValue", {
                    age: ageFromBirthYear(e.birth_year),
                  })
                : "—"}
            </span>
            <label className="flex items-center gap-1.5">
              <span className="text-admin-text3">
                {t("employeeDetail.genderLabel")}
              </span>
              <select
                value={e.gender}
                disabled={genderMut.isPending}
                onChange={(ev) => genderMut.mutate(ev.target.value as Gender)}
                className="bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-admin-text1 disabled:opacity-50 focus:outline-none focus:border-admin-cyan"
              >
                {GENDER_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {t(`common:gender.${v}`)}
                  </option>
                ))}
              </select>
            </label>
            <NumberEdit
              key={`h-${e.height_cm}`}
              label={t("employeeDetail.heightLabel")}
              suffix="cm"
              value={e.height_cm}
              min={50}
              max={250}
              pending={heightMut.isPending}
              onCommit={(v) => heightMut.mutate(v)}
            />
            <NumberEdit
              key={`w-${e.weight_kg}`}
              label={t("employeeDetail.weightLabel")}
              suffix="kg"
              value={Number(e.weight_kg)}
              min={20}
              max={250}
              step={0.1}
              pending={weightMut.isPending}
              onCommit={(v) => weightMut.mutate(v)}
            />
          </div>
          <div className="flex items-center gap-4 text-xs flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-admin-text3">{t("employeeDetail.bpGroupLabel")}</span>
              <select
                value={e.bp_group}
                disabled={bpMut.isPending}
                onChange={(ev) => bpMut.mutate(ev.target.value as BpGroup)}
                className="bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-admin-text1 disabled:opacity-50 focus:outline-none focus:border-admin-cyan"
              >
                {BP_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {t(`common:bpGroup.${v}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-admin-text3">{t("employeeDetail.siteLabel")}</span>
              <select
                value={e.site_id ?? ""}
                disabled={siteMut.isPending || sitesQ.isLoading}
                onChange={(ev) => {
                  // Empty option is render-only for the unassigned state; a null
                  // site_id wouldn't clear via the COALESCE patch, so ignore it.
                  if (ev.target.value) siteMut.mutate(ev.target.value);
                }}
                className="bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-admin-text1 disabled:opacity-50 focus:outline-none focus:border-admin-cyan"
              >
                <option value="">{t("employeeDetail.siteUnassigned")}</option>
                {(sitesQ.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Danger action: delete this employee (soft-delete + PDPA face erasure;
            measurements are kept). Opens a confirm dialog. */}
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="inline-flex items-center gap-1.5 self-start rounded border border-admin-red/40 bg-admin-red/10 px-3 py-1.5 text-sm text-admin-red hover:bg-admin-red/20"
        >
          <Trash2 className="w-4 h-4" /> {t("employees.delete.button")}
        </button>

        {/* Face enrollment (SPEC-019 / ADR-028): admin uploads a photo, attesting
            PDPA consent. Needs the face engine wired; otherwise the upload 503s.
            When already enrolled (face_enrolled, detail-only field) the card shows
            an enrolled badge and the action becomes "re-upload" (single active
            template — a new photo replaces the old). */}
        <div className="w-full sm:w-80 shrink-0 rounded border border-admin-border bg-admin-bg2 p-4">
          <div className="mb-3 text-sm font-medium text-admin-text1">
            {t("employeeDetail.faceLabel")}
          </div>
          {empQ.data?.face_enrolled && (
            <div className="mb-3 flex items-center gap-2 rounded border border-admin-green/40 bg-admin-green/10 px-3 py-2 text-sm font-semibold text-admin-green">
              <svg
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4 shrink-0"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.1 3.1 6.8-6.8a1 1 0 011.4 0z"
                  clipRule="evenodd"
                />
              </svg>
              {t("employeeDetail.faceEnrolledLabel")}
            </div>
          )}
          <label className="mb-3 flex items-start gap-2 text-xs text-admin-text2">
            <input
              type="checkbox"
              checked={faceConsent}
              onChange={(e) => setFaceConsent(e.target.checked)}
              className="mt-0.5"
            />
            <span>{t("employeeDetail.faceConsentLabel")}</span>
          </label>
          <label
            className={
              "inline-flex cursor-pointer items-center gap-2 rounded px-3 py-1.5 text-sm " +
              (faceConsent && !faceMut.isPending
                ? "bg-admin-cyan text-admin-bg hover:bg-admin-cyan/90"
                : "cursor-not-allowed bg-admin-bg3 text-admin-text3")
            }
          >
            {faceMut.isPending
              ? t("employeeDetail.faceUploading")
              : empQ.data?.face_enrolled
                ? t("employeeDetail.faceReuploadButton")
                : t("employeeDetail.faceUploadButton")}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={!faceConsent || faceMut.isPending}
              onChange={onPickFace}
            />
          </label>
        </div>

        <div className="flex border border-admin-border rounded overflow-hidden">
          {RANGE_VALUES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-sm transition-colors ${
                range === r
                  ? "bg-admin-cyan/15 text-admin-cyan"
                  : "text-admin-text2 hover:bg-admin-bg3"
              }`}
            >
              {t(`employeeDetail.ranges.${r}`)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab bar: 趨勢圖 (line charts) | 健康雷達 (radar). Range applies to both. */}
      <div className="flex border-b border-admin-border mb-4">
        {TAB_VALUES.map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => setTab(tb)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 transition-colors ${
              tab === tb
                ? "border-admin-cyan text-admin-cyan"
                : "border-transparent text-admin-text2 hover:text-admin-text1"
            }`}
          >
            {t(`employeeDetail.tabs.${tb}`)}
          </button>
        ))}
      </div>

      {trendQ.isLoading && (
        <div className="text-admin-text2 text-sm">{t("employeeDetail.trendsLoading")}</div>
      )}
      {trendQ.error && (
        <div className="text-admin-red text-sm">
          {t("employeeDetail.trendsFailed", { message: (trendQ.error as Error).message })}
        </div>
      )}

      {tab === "trends" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setExpanded(m.key)}
              aria-label={t("trends.zoom.open", { label: t(`trends.metrics.${m.key}`) })}
              className="text-left cursor-pointer rounded-lg focus:outline-none focus:ring-1 focus:ring-admin-cyan"
            >
              <TrendChart
                meta={m}
                series={seriesFor(m.key)}
                baseline={baselineFor(m.key)}
              />
            </button>
          ))}
        </div>
      ) : (
        // 6-axis health radar tab (ADR-018 / SPEC-015 §3.3). Single wide card —
        // the radar reads better large + centered, capped so it isn't absurd on
        // ultrawide. Click to zoom further.
        <div className="max-w-3xl mx-auto">
          <button
            type="button"
            onClick={() => setExpanded(RADAR_KEY)}
            aria-label={t("trends.zoom.open", { label: t("trends.radar.title") })}
            className="block w-full text-left cursor-pointer rounded-lg focus:outline-none focus:ring-1 focus:ring-admin-cyan"
          >
            <RadarCard series={seriesList} />
          </button>
        </div>
      )}

      <MetricModal
        metricKey={expanded}
        onClose={() => setExpanded(null)}
        series={seriesList}
        baselineFor={baselineFor}
      />

      {confirmDelete && (
        <DeleteEmployeeDialog
          targets={[{ id: e.id, name: e.name }]}
          onClose={() => setConfirmDelete(false)}
          onDeleted={() => navigate("/employees")}
        />
      )}
    </div>
  );
}

// Inline number editor that commits on blur / Enter (not per keystroke, so we
// don't fire a PATCH per digit). Reverts to the last saved value if the entry is
// blank or out of [min, max]; only fires onCommit when the value actually
// changed and is valid. Seeded from `value`; a successful save re-renders the
// parent with the new `value`, which the key={value} below picks up.
function NumberEdit({
  label,
  suffix,
  value,
  min,
  max,
  step,
  pending,
  onCommit,
}: {
  label: string;
  suffix: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  pending: boolean;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));

  function commit() {
    const n = Number(draft);
    if (draft.trim() === "" || Number.isNaN(n) || n < min || n > max) {
      setDraft(String(value)); // revert invalid entry
      return;
    }
    if (n !== value) onCommit(n);
  }

  return (
    <label className="flex items-center gap-1.5">
      <span className="text-admin-text3">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        min={min}
        max={max}
        step={step ?? 1}
        value={draft}
        disabled={pending}
        onChange={(ev) => setDraft(ev.target.value)}
        onBlur={commit}
        onKeyDown={(ev) => {
          if (ev.key === "Enter") ev.currentTarget.blur();
        }}
        className="w-20 bg-admin-bg3 border border-admin-border rounded px-2 py-1 text-admin-text1 disabled:opacity-50 focus:outline-none focus:border-admin-cyan"
      />
      <span className="text-admin-text3">{suffix}</span>
    </label>
  );
}
